/**
 * Evapotranspiration (ET) calculation using the Penman-Monteith formula
 * Reference: FAO-56 Penman-Monteith method
 */

// Unit conversion helpers
const fahrenheitToCelsius = (f) => (f - 32) * 5 / 9
const mphToMetersPerSecond = (mph) => mph * 0.44704

/**
 * Calculate reference evapotranspiration using Penman-Monteith formula
 *
 * @param {Object} params - Weather parameters
 * @param {number} params.maxTempF - Maximum temperature in Fahrenheit
 * @param {number} params.minTempF - Minimum temperature in Fahrenheit
 * @param {number} params.solarRadiationDailyJ - Daily total solar radiation in J/m²/day
 * @param {number} params.avgWindSpeedMPH - Average wind speed in MPH
 * @param {number} params.elevationMeters - Elevation in meters
 * @param {number} params.maxHumidity - Maximum relative humidity (%)
 * @param {number} params.minHumidity - Minimum relative humidity (%)
 * @param {number} params.latitude - Latitude in degrees
 * @param {Date} params.date - Date for calculation (defaults to current date)
 * @returns {Object|null} - { mm: ET in mm/day, inches: ET in inches/day } or null if inputs invalid
 */
export const calculateEvapotranspiration = ({
  maxTempF,
  minTempF,
  solarRadiationDailyJ,
  avgWindSpeedMPH,
  elevationMeters,
  maxHumidity,
  minHumidity,
  latitude,
  date = new Date()
}) => {
  // Input validation
  if (!maxTempF || !minTempF || !solarRadiationDailyJ || !avgWindSpeedMPH ||
      elevationMeters === null || elevationMeters === undefined ||
      !maxHumidity || !minHumidity || !latitude) {
    return null
  }

  // Constants
  const CANOPY_REFLECTION_COEFFICIENT = 0.23  // albedo for grass reference crop
  const STEFAN_BOLTZMANN = 4.903e-9  // MJ K^-4 m^-2 day^-1
  const SOLAR_CONSTANT = 0.0820  // MJ m^-2 min^-1

  // Unit conversions
  const maxTempC = fahrenheitToCelsius(maxTempF)
  const minTempC = fahrenheitToCelsius(minTempF)
  const avgTempC = (maxTempC + minTempC) / 2
  const windSpeedMs = mphToMetersPerSecond(avgWindSpeedMPH)

  // Convert solar radiation from J/m²/day to MJ/m²/day
  const solarRadiationMJ = solarRadiationDailyJ / 1000000

  // Atmospheric pressure (kPa) from elevation
  const atmosphericPressure = 101.3 * Math.pow((293 - 0.0065 * elevationMeters) / 293, 5.26)

  // Psychrometric constant (kPa/°C)
  const psychrometricConstant = 0.000665 * atmosphericPressure

  // Saturation vapor pressure (kPa)
  const satVaporPressureMax = 0.6108 * Math.exp((17.27 * maxTempC) / (maxTempC + 237.3))
  const satVaporPressureMin = 0.6108 * Math.exp((17.27 * minTempC) / (minTempC + 237.3))
  const satVaporPressure = (satVaporPressureMax + satVaporPressureMin) / 2

  // Actual vapor pressure (kPa) from humidity
  const actualVaporPressure = (
    satVaporPressureMin * (maxHumidity / 100) +
    satVaporPressureMax * (minHumidity / 100)
  ) / 2

  // Slope of saturation vapor pressure curve (kPa/°C)
  const slopeSatVaporPressure = (4098 * satVaporPressure) / Math.pow(avgTempC + 237.3, 2)

  // Day of year (Julian day)
  const startOfYear = new Date(date.getFullYear(), 0, 0)
  const diff = date - startOfYear
  const oneDay = 1000 * 60 * 60 * 24
  const dayOfYear = Math.floor(diff / oneDay)

  // Solar geometry
  const latitudeRad = latitude * Math.PI / 180
  const solarDeclination = 0.409 * Math.sin((2 * Math.PI / 365) * dayOfYear - 1.39)
  const sunsetHourAngle = Math.acos(-Math.tan(latitudeRad) * Math.tan(solarDeclination))

  // Inverse relative distance Earth-Sun
  const inverseRelDistance = 1 + 0.033 * Math.cos((2 * Math.PI / 365) * dayOfYear)

  // Extraterrestrial radiation (MJ m^-2 day^-1)
  const extraterrestrialRadiation = (24 * 60 / Math.PI) * SOLAR_CONSTANT * inverseRelDistance * (
    sunsetHourAngle * Math.sin(latitudeRad) * Math.sin(solarDeclination) +
    Math.cos(latitudeRad) * Math.cos(solarDeclination) * Math.sin(sunsetHourAngle)
  )

  // Clear sky radiation (MJ m^-2 day^-1)
  const clearSkyRadiation = (0.75 + 2e-5 * elevationMeters) * extraterrestrialRadiation

  // Net shortwave radiation (MJ m^-2 day^-1)
  const netShortwaveRadiation = (1 - CANOPY_REFLECTION_COEFFICIENT) * solarRadiationMJ

  // Net longwave radiation (MJ m^-2 day^-1)
  const maxTempK = maxTempC + 273.16
  const minTempK = minTempC + 273.16
  const netLongwaveRadiation = STEFAN_BOLTZMANN * (
    (Math.pow(maxTempK, 4) + Math.pow(minTempK, 4)) / 2
  ) * (0.34 - 0.14 * Math.sqrt(actualVaporPressure)) * (
    1.35 * Math.min(solarRadiationMJ / clearSkyRadiation, 1.0) - 0.35
  )

  // Net radiation (MJ m^-2 day^-1)
  const netRadiation = netShortwaveRadiation - netLongwaveRadiation

  // Soil heat flux (MJ m^-2 day^-1) - assumed negligible for daily calculations
  const soilHeatFlux = 0

  // Wind speed at 2m height - already provided, but ensure minimum
  const windSpeed2m = Math.max(windSpeedMs, 0.5)  // minimum wind speed to avoid division issues

  // Penman-Monteith equation
  const radiationTerm = 0.408 * slopeSatVaporPressure * (netRadiation - soilHeatFlux)
  const windTerm = psychrometricConstant * (900 / (avgTempC + 273)) * windSpeed2m * (satVaporPressure - actualVaporPressure)
  const denominator = slopeSatVaporPressure + psychrometricConstant * (1 + 0.34 * windSpeed2m)

  // Reference evapotranspiration (mm/day)
  const evapotranspirationMM = (radiationTerm + windTerm) / denominator

  // Ensure non-negative result
  const finalET = Math.max(0, evapotranspirationMM)

  return {
    mm: finalET,
    inches: finalET / 25.4
  }
}

// Support both ES6 and CommonJS for testing
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { calculateEvapotranspiration }
}
